import React from 'react';

import { useTranslation } from 'react-i18next';
import { AttentionMessage, VStack, Box, Flex } from '@tq/ui-components';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useIsMobile } from '@tq/ui-components/dist/hooks';
import {
  manageConsulteePageNumberState,
  manageConsulteePageSizeState,
  manageConsulteeSearchState,
  manageOrganisationUserTableEditingRowState,
  manageUserDeleteState,
  manageUserToDeleteState,
} from 'store';
import { Organisation } from 'types';
import { ManageTeamInviteUser, SearchText, UserTable } from 'components';
import { useUserTableColumns, useUserPanelRecords } from '../../hooks';
import { UserDeletionModal } from './UserDeletionModal';

export type UserPanelProps = {
  organisation?: Organisation;
};

export const UsersPanel: React.FC<UserPanelProps> = ({ organisation }) => {
  const { t } = useTranslation('create_organisation');
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = useRecoilState(manageUserDeleteState);
  const userToDelete = useRecoilValue(manageUserToDeleteState);

  const { recordCount, totalCount, teamOptions, users, paginationInfo } =
    useUserPanelRecords(organisation);

  const handleClose = () => {
    setShowModal(false);
  };

  const columns = useUserTableColumns(teamOptions);

  return (
    <VStack pb={4} pt={1} alignItems='stretch'>
      {!teamOptions?.length && (
        <AttentionMessage
          mb={3}
          status='warning'
          title={t(
            'users_panel_warning',
            'You must create an organisation and at least one team before adding users.'
          )}
          variant='inline'
        />
      )}
      <ManageTeamInviteUser
        disabled={!teamOptions?.length}
        buttonText={t('button_text_add', 'Add user')}
        teamOptions={teamOptions}
        pageType='ManageConsultees'
      />
      <Box marginBottom={5} width={isMobile ? '100%' : '30%'}>
        <SearchText
          margin={4}
          searchState={manageConsulteeSearchState}
          placeholderText={t('search_placeholder', 'Search name or email')}
          recordCount={recordCount}
          totalCount={totalCount}
        />
      </Box>
      <UserTable
        paginationInfo={paginationInfo}
        users={users}
        columns={columns}
        pageNumberState={manageConsulteePageNumberState}
        pageSizeState={manageConsulteePageSizeState}
        editingState={manageOrganisationUserTableEditingRowState}
      />
      {!users && (
        <Flex px={5}>
          <AttentionMessage
            mb={3}
            status='info'
            title={t('users_panel_no_data_info', 'No data')}
            variant='inline'
          />
        </Flex>
      )}
      {
        <>
          <UserDeletionModal
            isOpen={showModal}
            onClose={handleClose}
            userToDelete={userToDelete}
          />
        </>
      }
    </VStack>
  );
};
